import React from "react";
import { useTranslation } from "react-i18next";
import type { UserSociety } from "@/services/api/society/getUserSocieties";
import type { EventFormPayload } from "@/services/api/user/events";
import SocietyAvatar from "@/components/SocietyAvatar";
import ImageUploader from "@/components/ImageUploader";

export const MAX_EVENT_IMAGES = 10;

interface EventFormSocietyStepProps {
  societies: UserSociety[];
  payload: EventFormPayload;
  onChange: <K extends keyof EventFormPayload>(
    key: K,
    value: EventFormPayload[K],
  ) => void;
}

/**
 * Step 1 of the event wizard — organizer societies and cover images.
 * Mirrors the RN `SocietyAndImageScreen`.
 */
const EventFormSocietyStep: React.FC<EventFormSocietyStepProps> = ({
  societies,
  payload,
  onChange,
}) => {
  const { t } = useTranslation();

  const toggleSociety = (societyId: string) => {
    const next = payload.societyIds.includes(societyId)
      ? payload.societyIds.filter((id) => id !== societyId)
      : [...payload.societyIds, societyId];
    const universities = [
      ...new Set(
        next
          .map(
            (id) =>
              societies.find((society) => society.id === id)?.university ?? null,
          )
          .filter((code): code is string => Boolean(code)),
      ),
    ];
    onChange("societyIds", next);
    onChange("universityCodes", universities);
  };

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-1 text-[15px] font-bold text-gray-900">
          {t("Your Societies / Clubs*")}
        </h2>
        <p className="mb-3 text-xs text-gray-500">
          {t("Select your Societies / Clubs")}
        </p>

        {societies.length === 0 ? (
          <p className="rounded-xl bg-gray-50 py-4 text-center text-[13px] text-gray-500">
            {t("No societies yet")}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {societies.map((society) => {
              const active = payload.societyIds.includes(society.id);
              return (
                <button
                  key={society.membership_id ?? society.id}
                  type="button"
                  onClick={() => toggleSociety(society.id)}
                  className={`flex flex-col items-center rounded-2xl border p-3 transition ${
                    active
                      ? "border-[#ffa217] bg-[#fff8f0] shadow-sm"
                      : "border-gray-100 bg-white hover:border-[#ffa217]/50"
                  }`}
                >
                  <SocietyAvatar
                    icon={society.icon ?? undefined}
                    name={society.name}
                    className="h-12 w-12 text-lg"
                  />
                  <span className="mt-2 line-clamp-2 text-center text-[12px] font-semibold text-gray-700">
                    {society.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-[15px] font-bold text-gray-900">
            {t("Upload Event Image (max 10)")}
          </h2>
          <span className="text-xs text-gray-400">
            {payload.imageUrls.length}/{MAX_EVENT_IMAGES}
          </span>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          {t("The first image is used as the cover of your event.")}
        </p>

        <ImageUploader
          value={payload.imageUrls}
          onChange={(urls) => onChange("imageUrls", urls)}
          maxCount={MAX_EVENT_IMAGES}
        />
      </section>
    </div>
  );
};

export default EventFormSocietyStep;
